/**
 * Persiste e reaplica fundo virtual da webcam após disableVideo/enableVideo.
 *
 * O RealtimeKit perde middlewares de vídeo quando o track é recriado. Guardamos
 * a última middleware em memória (mesma aba) e, quando possível, blur/imagem
 * em sessionStorage para reaplicar via @cloudflare/realtimekit-virtual-background.
 */

import type RealtimeKitClient from "@cloudflare/realtimekit";
import RealtimeKitVideoBackgroundTransformer from "@cloudflare/realtimekit-virtual-background";

const STORAGE_KEY = "constela.virtualBackground.v1";

export type VirtualBackgroundPreference =
  | { mode: "blur"; blurStrength: number }
  | { mode: "virtual"; imageUrl: string }
  | { mode: "none" };

type VideoMiddleware = Parameters<
  RealtimeKitClient["self"]["addVideoMiddleware"]
>[0];

type MeetingSelf = RealtimeKitClient["self"] & {
  addVideoMiddleware: (mw: VideoMiddleware) => unknown;
  removeVideoMiddleware?: (mw: VideoMiddleware) => unknown;
  removeAllVideoMiddlewares?: () => unknown;
};

let lastMiddleware: VideoMiddleware | null = null;
let transformerPromise: Promise<RealtimeKitVideoBackgroundTransformer> | null =
  null;
const patchedMeetings = new WeakSet<object>();

function readPreference(): VirtualBackgroundPreference | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as VirtualBackgroundPreference;
    if (!parsed || typeof parsed !== "object" || !("mode" in parsed)) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function saveVirtualBackgroundPreference(
  preference: VirtualBackgroundPreference,
): void {
  try {
    if (preference.mode === "none") {
      sessionStorage.removeItem(STORAGE_KEY);
      lastMiddleware = null;
      return;
    }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(preference));
  } catch {
    // sessionStorage indisponível — ignora
  }
}

export function loadVirtualBackgroundPreference(): VirtualBackgroundPreference | null {
  return readPreference();
}

async function getTransformer(
  meeting: RealtimeKitClient,
): Promise<RealtimeKitVideoBackgroundTransformer | null> {
  if (!RealtimeKitVideoBackgroundTransformer.isSupported()) {
    return null;
  }
  if (!transformerPromise) {
    transformerPromise = RealtimeKitVideoBackgroundTransformer.init({
      meeting,
    }).catch((err) => {
      transformerPromise = null;
      throw err;
    });
  }
  try {
    return await transformerPromise;
  } catch (err) {
    console.warn("Falha ao iniciar VideoBackgroundTransformer:", err);
    return null;
  }
}

/**
 * Intercepta add/remove de middlewares para lembrar o efeito ativo na sessão.
 */
export function installVirtualBackgroundCapture(
  meeting: RealtimeKitClient,
): () => void {
  const self = meeting?.self as MeetingSelf | undefined;
  if (!self || patchedMeetings.has(self as object)) {
    return () => undefined;
  }

  patchedMeetings.add(self as object);

  const originalAdd = self.addVideoMiddleware.bind(self);
  const originalRemove = self.removeVideoMiddleware?.bind(self);
  const originalRemoveAll = self.removeAllVideoMiddlewares?.bind(self);

  self.addVideoMiddleware = (mw: VideoMiddleware) => {
    lastMiddleware = mw;
    return originalAdd(mw);
  };

  if (originalRemove) {
    self.removeVideoMiddleware = (mw: VideoMiddleware) => {
      if (lastMiddleware === mw) {
        lastMiddleware = null;
      }
      return originalRemove(mw);
    };
  }

  if (originalRemoveAll) {
    self.removeAllVideoMiddlewares = () => {
      lastMiddleware = null;
      saveVirtualBackgroundPreference({ mode: "none" });
      return originalRemoveAll();
    };
  }

  return () => {
    self.addVideoMiddleware = originalAdd;
    if (originalRemove) {
      self.removeVideoMiddleware = originalRemove;
    }
    if (originalRemoveAll) {
      self.removeAllVideoMiddlewares = originalRemoveAll;
    }
    patchedMeetings.delete(self as object);
  };
}

export type ReapplyVirtualBackgroundResult =
  | "reapplied"
  | "none"
  | "unsupported"
  | "failed";

/**
 * Reaplica o último fundo conhecido (middleware em memória ou preferência salva).
 */
export async function reapplyVirtualBackground(
  meeting: RealtimeKitClient,
): Promise<ReapplyVirtualBackgroundResult> {
  const self = meeting?.self as MeetingSelf | undefined;
  if (!self) {
    return "none";
  }

  try {
    if (lastMiddleware) {
      try {
        self.removeAllVideoMiddlewares?.();
      } catch {
        // ignore
      }
      self.addVideoMiddleware(lastMiddleware);
      return "reapplied";
    }

    const preference = readPreference();
    if (!preference || preference.mode === "none") {
      return "none";
    }

    if (!RealtimeKitVideoBackgroundTransformer.isSupported()) {
      return "unsupported";
    }

    const transformer = await getTransformer(meeting);
    if (!transformer) {
      return "failed";
    }

    try {
      self.removeAllVideoMiddlewares?.();
    } catch {
      // ignore
    }

    if (preference.mode === "blur") {
      const mw = await transformer.createBackgroundBlurVideoMiddleware(
        preference.blurStrength,
      );
      lastMiddleware = mw;
      self.addVideoMiddleware(mw);
      return "reapplied";
    }

    const mw = await transformer.createStaticBackgroundVideoMiddleware(
      preference.imageUrl,
    );
    lastMiddleware = mw;
    self.addVideoMiddleware(mw);
    return "reapplied";
  } catch (err) {
    console.warn("Falha ao reaplicar fundo virtual:", err);
    return "failed";
  }
}

/**
 * Liga o vídeo e tenta restaurar o fundo virtual em seguida.
 * @returns true se vale mostrar o toast pedindo para reativar o fundo manualmente
 */
export async function enableVideoAndRestoreBackground(
  meeting: RealtimeKitClient,
): Promise<{ enabled: boolean; suggestManualRestore: boolean }> {
  const self = meeting?.self;
  if (!self) {
    return { enabled: false, suggestManualRestore: false };
  }

  const hadEffectHint =
    lastMiddleware != null ||
    (() => {
      const pref = readPreference();
      return !!pref && pref.mode !== "none";
    })();

  try {
    await Promise.resolve(self.enableVideo());
  } catch {
    return { enabled: false, suggestManualRestore: false };
  }

  // Pequeno atraso para o track estabilizar antes do middleware.
  await new Promise((r) => window.setTimeout(r, 250));

  const result = await reapplyVirtualBackground(meeting);
  if (result === "reapplied") {
    return { enabled: true, suggestManualRestore: false };
  }

  // Tinha efeito (ou usuário costuma usar fundo) e não conseguimos reaplicar.
  return {
    enabled: true,
    suggestManualRestore: hadEffectHint || result === "unsupported",
  };
}

export function hadVirtualBackgroundInSession(): boolean {
  if (lastMiddleware != null) return true;
  const pref = readPreference();
  return !!pref && pref.mode !== "none";
}
